import React, { useEffect, useState } from "react";
import { Card, Typography, Spin, Alert } from "antd";
import { useParams } from "react-router-dom";
import { DollarOutlined } from "@ant-design/icons";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const CombinedPayments = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [totalPaid, setTotalPaid] = useState(0);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    apiClient.get(`/customers/${id}/payments`)
      .then((paymentsRes) => {
        const paymentsData = paymentsRes.data;
        setPayments(paymentsData.payments || []);
        setCustomerName(paymentsData.customer_name || "");
        setTotalPaid(toApiNumber(paymentsData.orders_total_paid, 0));
      })
      .catch(() => {
        setError(t("paymentHistoryLoadFailed", "Failed to load payments."));
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>{t("invalidCustomerId", "Invalid or missing customer ID")}</h1>
      </div>
    );
  }

  // Calculate sum of all payments
  const totalPayments = payments.reduce((sum, p) => sum + toApiNumber(p.amount, 0), 0);
  // Calculate sum of both payments and paid orders
  const totalCombined = totalPayments + toApiNumber(totalPaid, 0);

  // Handlers for card clicks
  const handlePaymentsClick = () => {
    window.location.href = `/customers/${id}/payments`;
  };
  const handleOrdersClick = () => {
    window.location.href = `/customers/${id}`;
  };

  return (
    <Card style={{ margin: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 16 }}>
        <DollarOutlined style={{ color: "#17a2b8", fontSize: 32 }} />
        <div>
          <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
            {t("combinedPayments", "Combined Payments")}
          </Title>
          <div style={{ fontSize: 16, color: '#888', fontWeight: 500 }}>
            {customerName && <span>{t("customer", "Customer")}: <span style={{ color: '#222', fontWeight: 600 }}>{customerName}</span></span>}
          </div>
        </div>
      </div>
      {loading ? (
        <Spin size="large" />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : (
        <div style={{ display: "flex", gap: 24, marginBottom: 24, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          <Card
            hoverable
            onClick={handlePaymentsClick}
            style={{ background: "#e6f7ff", borderColor: "#91d5ff", minWidth: 220, cursor: 'pointer', boxShadow: '0 2px 8px #e6f7ff', flex: 1 }}
            variant="borderless"
            bodyStyle={{ padding: 20 }}
          >
            <Title level={5} style={{ color: "#1890ff", margin: 0 }}>{t("totalPayments", "Total Payments")}</Title>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1890ff" }}>{formatCurrency(totalPayments)}</div>
            <div style={{ fontSize: 13, color: '#1890ff', marginTop: 4 }}>{t("viewAllPayments", "View all payments")}</div>
          </Card>
          <Card
            hoverable
            onClick={handleOrdersClick}
            style={{ background: "#fffbe6", borderColor: "#ffe58f", minWidth: 220, cursor: 'pointer', boxShadow: '0 2px 8px #fffbe6', flex: 1 }}
            variant="borderless"
            bodyStyle={{ padding: 20 }}
          >
            <Title level={5} style={{ color: "#d48806", margin: 0 }}>{t("totalPaidOrders", "Total Paid Orders")}</Title>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#d48806" }}>{formatCurrency(totalPaid)}</div>
            <div style={{ fontSize: 13, color: '#d48806', marginTop: 4 }}>{t("viewAllOrders", "View all orders")}</div>
          </Card>
          <Card style={{ background: "#f0f5ff", borderColor: "#91d5ff", minWidth: 220, flex: 1 }} variant="borderless" bodyStyle={{ padding: 20 }}>
            <Title level={5} style={{ color: "#0050b3", margin: 0 }}>{t("combinedPaymentsTotal", "Total Payments + Paid Orders")}</Title>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0050b3" }}>{formatCurrency(totalCombined)}</div>
          </Card>
        </div>
      )}
    </Card>
  );
};

export default CombinedPayments;

